import React, { FC, useState } from 'react';
import { Text, View } from 'react-native';
import ColorWrapper, { WithColor } from '@styles/colors';
import { getMaxOPZoom, scale, scaleOP } from '@components/frame';
import { isWeb, navigateBack, navigateTo, navigationRef, Popup } from '@helpers';
import { goToMainApp } from '@components/operatorPanel/helpers/goToMainApp';
import OperatorPanelSettingsActionsModal from './components/OperatorPanelSettingsActionsModal';
import Loading from '@components/loading';
import { fontOP, initFontConfig } from '@styles/fonts';
import TouchableElement from '@components/common/touchableElement';
import i18n from 'i18n-js';
import Svg from '@styles/icons/svg';
import { getBoxShadowStyle } from '@styles/shadow';
import { IconConfig } from '@components/operatorPanel/interfaces';
import { OperatorPanelSettingsButton } from '@components/operatorPanel/components/elements/buttons/OperatorPanelSettingsButton';
import { getLanguageTileByValue } from '@components/settings/components/modules/changeLanguage';
import OperatorPanelSettingsWrapper from './components/OperatorPanelSettingsWrapper';
import ChangeLanguageModal from '@components/settings/components/modules/changeLanguage';
import ChangeThemeModal from '@components/settings/components/modules/changeTheme';
import UserImage from '@components/common/UserImage';
import OperatorPanelSettingsTextSizeModal from './components/OperatorPanelSettingsTextSizeModal';
import { connect } from 'react-redux';
import { SimpleOnPress } from '@interfaces/base';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';

interface OperatorPanelSettingsProps extends WithColor {
  lg: string;
  user: {
    userInfo?: {
      userProfile?: {
        firstName?: string;
        lastName?: string;
      };
    };
  };
}

const OperatorPanelSettings: FC<OperatorPanelSettingsProps> = ({ C, lg, user: { userInfo = {} }, ...rest }) => {
  const route = navigationRef?.current?.getCurrentRoute();

  const [rerenderSettingsPage, setRerenderSettingsPage] = useState<boolean>(false);

  const initFontConfigAndForceRerenderSettingsPage = () => {
    setRerenderSettingsPage((prev) => !prev);
    initFontConfig(false, true);
  };

  const [showOperatorPanelSettingsActionModal, setShowOperatorPanelSettingsActionModal] = useState<boolean>(false);
  const [logoutInProgress, setLogoutInProgress] = useState<boolean>(false);

  const boxShadowStyle = getBoxShadowStyle({ type: 'Very Wide', scheme: 'light' });
  const language = getLanguageTileByValue(lg);

  const onClose = () => navigateBack('OperatorPanel', { id: route?.params?.id });

  const onLeaveMachinePress = () => {
    navigateTo('OperatorPanelSettingsMachineSelect', { id: route?.params?.id });
  };

  const onAddDevicePress = () => {
    navigateTo('OperatorPanelSettingsAddDevice', { id: route?.params?.id });
  };

  const onMoreButtonPress = () => setShowOperatorPanelSettingsActionModal(true);

  const toggleChangeLanguage = () => {
    Popup({
      Component: (p) => <ChangeLanguageModal {...rest} lg={lg} {...p} />,
    });
  };

  const toggleChangeTheme = () => {
    Popup({
      Component: (p) => <ChangeThemeModal {...rest} {...p} />,
    });
  };

  const toggleChangeSize = () => {
    Popup({
      Component: (p) => (
        <OperatorPanelSettingsTextSizeModal C={C} {...p} forceRerenderSettingsPage={initFontConfigAndForceRerenderSettingsPage} font={fontOP} />
      ),
      width: scale(700),
    });
  };

  const onGoToMainAppPress = () => goToMainApp();

  const maxOPZoom = getMaxOPZoom();
  const shouldHideTextScalingButton = !isWeb && maxOPZoom === 0;

  const bottomButtonsConfig: (IconConfig & SimpleOnPress & { text: string })[]  = [
    { width: scaleOP(32), height: scaleOP(32), icon: 'language', color: C('Label/Light/Unselected'), onPress: toggleChangeLanguage, text: language },
    ...(!shouldHideTextScalingButton
      ? [
          {
            width: scaleOP(32),
            height: scaleOP(32),
            icon: 'textSize',
            color: C('Label/Light/Unselected'),
            onPress: toggleChangeSize,
            text: i18n.t('Size'),
          },
        ]
      : []),
    {
      width: scaleOP(32),
      height: scaleOP(32),
      icon: 'appearance',
      color: C('Label/Light/Unselected'),
      onPress: toggleChangeTheme,
      text: i18n.t('Appearance'),
    },
    {
      width: scaleOP(32),
      height: scaleOP(32),
      icon: 'qrCode',
      color: C('Label/Light/Unselected'),
      onPress: onAddDevicePress,
      text: i18n.t('Add Your Phone'),
    },
  ];

  const { userProfile = {} } = userInfo;
  const { firstName = ' ', lastName = ' ' } = userProfile;

  return (
    <OperatorPanelSettingsWrapper.Root C={C} scale={scaleOP} font={fontOP}>
      <OperatorPanelSettingsWrapper.Header>
        <OperatorPanelSettingsWrapper.HeaderLeftSide>
          <OperatorPanelSettingsWrapper.MoreButton onPress={onMoreButtonPress} testID={TEST_IDS.operatorPanel.settings.moreButton} />
        </OperatorPanelSettingsWrapper.HeaderLeftSide>
        <OperatorPanelSettingsWrapper.Title text="" />
        <OperatorPanelSettingsWrapper.HeaderRightSide containerStyles={{ gap: scale(20) }}>
          <OperatorPanelSettingsWrapper.ActionButton onPress={onGoToMainAppPress} testID={TEST_IDS.operatorPanel.settings.goToMainAppButton}>
            {i18n.t('Go To Main App')}
          </OperatorPanelSettingsWrapper.ActionButton>
          <OperatorPanelSettingsWrapper.CloseButton onPress={onClose} testID={TEST_IDS.operatorPanel.settings.closeButton} />
        </OperatorPanelSettingsWrapper.HeaderRightSide>
      </OperatorPanelSettingsWrapper.Header>

      <OperatorPanelSettingsWrapper.Content>
        {logoutInProgress && (
          <View style={{ position: 'absolute', zIndex: 100000, width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center' }}>
            <Loading />
          </View>
        )}

        {showOperatorPanelSettingsActionModal && (
          <OperatorPanelSettingsActionsModal
            C={C}
            setShowOperatorPanelSettingsActionModal={setShowOperatorPanelSettingsActionModal}
            setLogoutInProgress={setLogoutInProgress}
            scale={scaleOP}
          />
        )}

        <View style={[{ position: 'absolute', top: scaleOP(40), alignItems: 'center', paddingHorizontal: scaleOP(20), gap: scaleOP(18) }]}>
          <UserImage {...{ showAdd: false, size: scaleOP(90), scale: scaleOP, font: fontOP }} />
          <Text style={[fontOP['Title 1'], { textAlign: 'center', color: C('Label/Light/Primary') }]}>
            {firstName} {lastName}
          </Text>
        </View>

        <TouchableElement onPress={onLeaveMachinePress}>
          {({ boxStyle }) => (
            <View
              style={[
                {
                  ...boxStyle,
                  backgroundColor: C('Button/Light/PrimaryMono'),
                  paddingVertical: scaleOP(16),
                  paddingHorizontal: scaleOP(18),
                  borderRadius: scaleOP(18),
                  width: scaleOP(480),
                  height: scaleOP(96),
                  gap: scaleOP(6),
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  ...boxShadowStyle,
                },
              ]}
            >
              <View style={{ paddingVertical: scaleOP(6), flexDirection: 'row', gap: scaleOP(9), justifyContent: 'center', alignItems: 'center' }}>
                <Svg height={scaleOP(24)} width={scaleOP(24)} color={C('Button/Light/PrimaryMono Text')} icon="sendToMachine" />
                <Svg height={scaleOP(24)} width={scaleOP(15)} color={C('Button/Light/PrimaryMono Text')} icon="walkingMan" />
              </View>
              <Text style={[fontOP['Headline Large'], { color: C('Button/Light/PrimaryMono Text') }]}>{i18n.t('Leave Machine')}</Text>
            </View>
          )}
        </TouchableElement>

        <View style={{ position: 'absolute', bottom: scaleOP(48), width: scaleOP(480), flexDirection: 'row', justifyContent: 'center' }}>
          {bottomButtonsConfig.map((c) => (
            <View key={c.icon} style={{ alignItems: 'center', width: scaleOP(138), gap: scaleOP(21) }}>
              <OperatorPanelSettingsButton iconConfig={c} C={C} onPress={c.onPress} scale={scaleOP} />
              <Text style={[fontOP['Button 1'], { color: C('Label/Light/Tertiary'), textAlign: 'center' }]}>{c.text}</Text>
            </View>
          ))}
        </View>
      </OperatorPanelSettingsWrapper.Content>
    </OperatorPanelSettingsWrapper.Root>
  );
};

export default connect(({ user }) => ({ user }), {})(ColorWrapper(OperatorPanelSettings));
